/**
 * GemShop Personalization — upload + editor circular + campos de texto por variante.
 *
 * Sem dependencias externas. Compativel com o tema Glozin 2.6 e com qualquer
 * tema cujo formulario de produto tenha action /cart/add e input[name="id"].
 * Deteccao de variante: eventos 'change' no formulario + poll leve de fallback
 * (nao depende de eventos customizados do tema).
 *
 * Propriedades enviadas ao carrinho (nomes vem de cfg.props, com defaults PT):
 *   properties[Foto personalizada]  -> link publico https://files.gemshop.com.br/f/{token}
 *                                      (produto com 1 foto, photoCount<=1 — padrao de ~90 produtos)
 *   properties[Foto 1], [Foto 2]... -> quando photoCount>1 (ex.: pulseira Duo), 1 property por slot
 *   properties[_GemShop Upload ID]  -> UUID interno (oculto no checkout pelo "_"); com sufixo
 *                                      " {n}" quando photoCount>1
 *   properties[Texto lado 1..4]     -> textos de gravacao
 *   properties[Link Spotify]        -> URL validada do Spotify
 * Em testMode, todos os nomes visiveis ganham prefixo "_Teste " (ficam ocultos
 * para o cliente e nao conflitam com o app atual).
 *
 * v2: modal pre-carrinho (estilo Wear Felicity), editor configuravel
 * (editorEnabled=false envia direto com recorte central; editorTools controla
 * zoom/rotacao) e i18n/props por idioma vindos do snippet Liquid.
 *
 * v3.3: campos de texto nao aceitam emoji. Em acessorio com gravacao de texto,
 * o emoji digitado ou colado vira o coracao ❤ (U+2764), unico simbolo que a
 * gravacao reproduz; no produto de codigo morse (morseEnabled) o emoji e
 * simplesmente REMOVIDO, pois nao ha morse para simbolo, e NUMERO tambem nao
 * e aceito (a peca de morse so monta letras). Aplicado na digitacao e de
 * novo antes de montar a property (Widget.prototype.cleanText).
 *
 * v3.7: Morse aceita so o alfabeto ocidental (letra que sem acento vira A-Z);
 * o que for descartado mostra "use apenas letras de A a Z" sob o campo.
 *
 * v3.6: aviso ao adicionar acessorio com gravacao com lado(s) de texto em
 * branco - nao bloqueia: "o pingente vai no estilo original, mais discreto,
 * deseja continuar?" com Escrever o texto / Continuar sem texto. Fora do
 * Morse (la a frase e a propria peca). Sem traducao no idioma, nao aparece.
 *
 * v3.5: upload mais rapido - a foto original (copia de seguranca privada no
 * R2) sobe reduzida a 2048 px no lado maior em vez do arquivo cru do celular
 * (3-12 MB), gerada em paralelo com o recorte; miniatura de 240 px.
 *
 * v3.4: ao terminar o upload a peca vai direto para o carrinho (um clique a
 * menos). So acontece quando nao falta nada ao cliente: todas as fotos do
 * produto enviadas e sem campo de texto/Spotify em aberto (ver
 * canAutoAddAfterUpload). O aviso de resolucao baixa foi removido do editor.
 *
 * v3.2: multiplas fotos (cfg.photoCount>1, ex.: 2 no produto "Pulseira Duo").
 * Cada slot tem seu proprio botao/preview/erro (this.photoSlots[]); o modal
 * pre-carrinho (que assume 1 foto) so roda quando photoCount<=1 — produtos
 * multi-foto usam validacao inline direta. photoCount e fixo por produto
 * (nao varia por variante) para nao precisar reconstruir os slots ao trocar
 * a variante.
 */
(function () {
  'use strict';

  var MAX_BYTES = 15 * 1024 * 1024;
  var ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,.jpg,.jpeg,.png,.webp';

  // ---------------------------------------------------------------- utils --

  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'text') node.textContent = attrs[k];
      else if (k === 'html') node.innerHTML = attrs[k]; // apenas strings estaticas internas
      else if (attrs[k] !== null && attrs[k] !== undefined) node.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) { node.appendChild(c); });
    return node;
  }

  function sessionId() {
    try {
      var id = localStorage.getItem('gs_session_id');
      if (!id) {
        id = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2));
        localStorage.setItem('gs_session_id', id);
      }
      return id;
    } catch (e) { return 'no-storage'; }
  }

  function isSpotifyUrl(value) {
    try {
      var u = new URL(value.trim());
      if (u.protocol !== 'https:') return false;
      if (u.hostname === 'spotify.link') return true;
      if (u.hostname !== 'open.spotify.com') return false;
      return /^\/(intl-[a-z-]+\/)?(track|album|playlist|episode|artist)\/[A-Za-z0-9]+/.test(u.pathname);
    } catch (e) { return false; }
  }

  // ------------------------------------------------------------- emoji ----

  // A gravacao so reproduz um simbolo grafico: o coracao ❤ (U+2764). Qualquer
  // outro emoji digitado ou colado vira esse coracao, em vez de sair como
  // caixa vazia na peca. Sequencias compostas (familia unida por ZWJ, tom de
  // pele, bandeira, tecla numerica) contam como UM emoji e viram UM coracao.
  var HEART = '\u2764';

  var EMOJI_RE = (function () {
    try {
      return new RegExp(
        '(?:[#*0-9]\\uFE0F?\\u20E3)' +                                  // teclas: 1 + U+20E3
        '|(?:[\\u{1F1E6}-\\u{1F1FF}]{2})' +                             // bandeiras
        '|(?:\\p{Extended_Pictographic}' +
          '(?:\\p{Emoji_Modifier}|\\uFE0F|\\u200D\\p{Extended_Pictographic})*)',
        'gu'
      );
    } catch (e) {
      // navegador sem property escapes (pre-ES2018): cobre o essencial sem
      // derrubar o widget; aqui um bloco contiguo vira um unico coracao
      return /(?:[\uD800-\uDBFF][\uDC00-\uDFFF]|[\u2190-\u2BFF\uFE0F\u20E3])+/g;
    }
  })();

  // Produto normal: emoji vira coracao. Produto de codigo morse: emoji e
  // REMOVIDO (nem o coracao entra) - nao existe morse para simbolo, entao um
  // coracao na "Frase" ficaria sem par no "Codigo Morse" e confundiria a Sihi.
  function replaceEmoji(s, replacement) {
    if (typeof s !== 'string' || !s) return s;
    return s.replace(EMOJI_RE, replacement).replace(/[\uFE0F\u200D]/g, '');
  }

  // No morse so entra o ALFABETO OCIDENTAL: letra que, sem acento, vira A-Z
  // (regra do Pedro, 30/09/2026: uma cliente escreveu em grego e o pedido
  // chegou sem codigo morse). Acento passa (e, c, a com til viram a letra base
  // no calculo); grego, cirilico, japones, numero, pontuacao e emoji saem. Letras
  // como ss alemao e o cortado nao tem morse e tambem saem. Fica o "&" (tem
  // codigo morse e e caso real: MARIA & JOAO) e o espaco.
  function onlyMorseLetters(s) {
    var out = '';
    for (var i = 0; i < s.length; i++) {
      var ch = s.charAt(i);
      if (ch === '&' || /\s/.test(ch)) { out += ch; continue; }
      var base = stripDiacritics(ch).toUpperCase();
      if (base.length === 1 && base >= 'A' && base <= 'Z') out += ch;
    }
    return out;
  }

  // --------------------------------------------------------- codigo morse --

  var MORSE_TABLE = {
    A: '.-', B: '-...', C: '-.-.', D: '-..', E: '.', F: '..-.', G: '--.', H: '....',
    I: '..', J: '.---', K: '-.-', L: '.-..', M: '--', N: '-.', O: '---', P: '.--.',
    Q: '--.-', R: '.-.', S: '...', T: '-', U: '..-', V: '...-', W: '.--', X: '-..-',
    Y: '-.--', Z: '--..',
    '0': '-----', '1': '.----', '2': '..---', '3': '...--', '4': '....-',
    '5': '.....', '6': '-....', '7': '--...', '8': '---..', '9': '----.',
    '.': '.-.-.-', ',': '--..--', '?': '..--..', "'": '.----.', '!': '-.-.--',
    '/': '-..-.', '(': '-.--.', ')': '-.--.-', '&': '.-...', ':': '---...',
    ';': '-.-.-.', '=': '-...-', '+': '.-.-.', '-': '-....-', '_': '..--.-',
    '"': '.-..-.', '$': '...-..-', '@': '.--.-.',
  };

  // remove acentos (á->a, ç->c...) so para calcular o codigo morse; o texto
  // digitado pelo cliente e gravado exatamente como escrito na property
  function stripDiacritics(s) {
    return s.normalize ? s.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : s;
  }

  /** letras separadas por espaco; palavras separadas por " / " (notacao padrao). */
  function textToMorse(str) {
    var norm = stripDiacritics(String(str || '').toUpperCase());
    var words = norm.split(/\s+/).filter(Boolean);
    var out = [];
    words.forEach(function (word) {
      var letters = [];
      for (var i = 0; i < word.length; i++) {
        var code = MORSE_TABLE[word.charAt(i)];
        if (code) letters.push(code);
      }
      if (letters.length) out.push(letters.join(' '));
    });
    return out.join(' / ');
  }

  /** atualiza a caixa de leitura da traducao morse, com estado vazio estilizado. */
  function updateMorseOutput(node, value, emptyText) {
    var code = textToMorse(value);
    node.textContent = code || (emptyText || '');
    node.classList.toggle('gs-morse-output--empty', !code);
  }

  // ------------------------------------------------------------ Widget ----

  // o filtro | t do Shopify escapa HTML (& vira &amp;); desfaz para exibicao
  function decodeEntities(s) {
    if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
    var ta = document.createElement('textarea');
    ta.innerHTML = s;
    return ta.value;
  }

  function Widget(container) {
    var cfgEl = qs(container, '[data-gs-config]');
    if (!cfgEl) return;
    this.cfg = JSON.parse(cfgEl.textContent);
    var i18n = this.cfg.i18n || {};
    for (var k in i18n) if (Object.prototype.hasOwnProperty.call(i18n, k)) i18n[k] = decodeEntities(i18n[k]);
    this.root = qs(container, '[data-gs-root]');
    this.container = container;
    this.form = this.findProductForm();
    this.photoSlots = [];        // 1 item por foto (1 hoje na maioria; >1 = ex. Pulseira Duo)
    this.uploading = false;
    this.currentVariantId = String(this.cfg.initialVariantId || this.readVariantId() || '');
    this.spotifyChecked = false;
    this.textValues = {};        // indice (1..4) -> valor
    this.spotifyValue = '';
    this.skipOnce = false;       // "pular e adicionar" autorizado uma unica vez
    this.emptyTextOk = false;    // cliente ja confirmou seguir com lado(s) sem texto
    this.pendingAddToCart = false; // fluxo modal: apos confirmar a foto, adiciona sozinho
    this.autoAddDone = false;      // trava para nao adicionar 2x se trocar a foto
    this.build();
    this.watchVariant();
    this.hookSubmit();
    this.root.hidden = false;
  }

  Widget.prototype.propName = function (name) {
    if (name.charAt(0) === '_') return name; // ja oculto
    return this.cfg.testMode ? '_Teste ' + name : name;
  };

  /** Nome de uma property do carrinho (localizavel via cfg.props; defaults PT). */
  Widget.prototype.prop = function (key, n) {
    var defaults = {
      photo: 'Foto personalizada',
      uploadId: '_GemShop Upload ID',
      photoSlot: 'Foto {n}',
      uploadIdSlot: '_GemShop Upload ID {n}',
      textSide: 'Texto lado {n}',
      spotify: 'Link Spotify',
      noPhoto: 'Foto pendente',
      noPhotoValue: '⚠️',
      morseCode: 'Código Morse',
    };
    var name = (this.cfg.props || {})[key] || defaults[key];
    return n !== undefined ? name.replace('{n}', n) : name;
  };

  /** Quantidade de fotos do produto (fixa; nao varia por variante). Minimo 1. */
  Widget.prototype.photoCount = function () {
    var n = parseInt(this.cfg.photoCount, 10);
    return n > 0 ? n : 1;
  };

  Widget.prototype.findProductForm = function () {
    var pid = String(this.cfg.productId);
    var forms = qsa(document, 'form[action*="/cart/add"]');
    // prefere o formulario visivel cujo product-id combine
    var match = forms.filter(function (f) {
      var input = f.querySelector('input[name="product-id"]');
      return input && String(input.value) === pid;
    });
    var candidates = match.length ? match : forms;
    // todos os forms do produto (temas com sticky bar tem um segundo form):
    // properties e gate valem para TODOS, senao o sticky compra sem foto
    this.forms = candidates;
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].offsetParent) return candidates[i];
    }
    return candidates[0] || null;
  };

  Widget.prototype.allForms = function () {
    if (this.forms && this.forms.length) return this.forms;
    return this.form ? [this.form] : [];
  };

  Widget.prototype.readVariantId = function () {
    if (!this.form) return null;
    var input = this.form.querySelector('[name="id"]');
    return input ? String(input.value) : null;
  };

  Widget.prototype.variantCfg = function () {
    var v = (this.cfg.variants || {})[this.currentVariantId] || {};
    var spotifyEnabled = this.cfg.spotifyEnabled;
    if (v.spotifyOverride === true || v.spotifyOverride === false) spotifyEnabled = v.spotifyOverride;
    var required = this.cfg.photoRequired;
    if (v.requiredOverride === true || v.requiredOverride === false) required = v.requiredOverride;
    var textCount = 0;
    if (this.cfg.textEnabled) {
      textCount = (typeof v.textFieldCount === 'number') ? v.textFieldCount : 1;
    }
    return { textCount: textCount, spotifyEnabled: spotifyEnabled, photoRequired: required };
  };

  // ------------------------------------------------------------- markup ---

  Widget.prototype.build = function () {
    var self = this;
    this.root.innerHTML = '';

    // textos/Spotify ACIMA do botao de foto (pedido do Pedro)
    this.textWrap = el('div', { class: 'gs-texts' });
    this.root.appendChild(this.textWrap);

    this.photoSlots = [];
    this.photoGroupError = null;
    if (this.cfg.photoEnabled) {
      var count = this.photoCount();
      var multi = count > 1;
      var photosWrap = multi ? el('div', { class: 'gs-photo-slots' }) : this.root;
      for (var i = 1; i <= count; i++) {
        var slot = this.buildPhotoSlot(i, multi);
        this.photoSlots.push(slot);
        photosWrap.appendChild(slot.wrap);
      }
      if (multi) {
        this.root.appendChild(photosWrap);
        this.photoGroupError = el('p', { class: 'gs-error', role: 'alert', hidden: '' });
        this.root.appendChild(this.photoGroupError);
      }
    }

    this.renderTextFields();
    this.syncFormProperties();
  };

  /** Constroi um slot de foto (botao+preview+erro). showLabel=true quando ha mais de 1 (ex.: "Foto 1"). */
  Widget.prototype.buildPhotoSlot = function (index, showLabel) {
    var i18n = this.cfg.i18n;
    var self = this;
    var slot = { index: index, upload: null };

    slot.fileInput = el('input', {
      type: 'file', accept: ACCEPT, class: 'gs-visually-hidden',
      'aria-label': i18n.uploadButton, tabindex: '-1',
    });
    slot.uploadBtn = el('button', { type: 'button', class: 'gs-upload-btn', 'data-gs-upload-btn': '' });
    slot.uploadBtn.appendChild(el('span', { class: 'gs-upload-btn__icon', 'aria-hidden': 'true', html: svgCamera() }));
    slot.uploadBtnText = el('span', { text: i18n.uploadButton });
    slot.uploadBtn.appendChild(slot.uploadBtnText);

    slot.preview = el('div', { class: 'gs-preview', hidden: '' });
    slot.errorBox = el('p', { class: 'gs-error', role: 'alert', hidden: '' });

    var photoWrap = el('div', { class: 'gs-photo' }, [slot.fileInput, slot.uploadBtn, slot.preview, slot.errorBox]);
    if (showLabel) {
      var label = (i18n.photoSlotLabel || 'Photo {n}').replace('{n}', index);
      slot.wrap = el('div', { class: 'gs-photo-slot' }, [
        el('div', { class: 'gs-field__label', text: label }),
        photoWrap,
      ]);
    } else {
      slot.wrap = photoWrap;
    }

    slot.uploadBtn.addEventListener('click', function () {
      self.pendingAddToCart = false; // fluxo normal: confirmar foto NAO adiciona sozinho
      slot.fileInput.click();
    });
    slot.fileInput.addEventListener('change', function () {
      var file = slot.fileInput.files && slot.fileInput.files[0];
      // limpa o input para que escolher o MESMO arquivo dispare change de novo
      slot.fileInput.value = '';
      if (file) self.onFileChosen(file, slot);
    });

    // drag & drop no botao (desktop)
    ['dragover', 'dragenter'].forEach(function (evt) {
      photoWrap.addEventListener(evt, function (e) { e.preventDefault(); photoWrap.classList.add('gs-drag'); });
    });
    ['dragleave', 'drop'].forEach(function (evt) {
      photoWrap.addEventListener(evt, function (e) { e.preventDefault(); photoWrap.classList.remove('gs-drag'); });
    });
    photoWrap.addEventListener('drop', function (e) {
      var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) self.onFileChosen(f, slot);
    });

    return slot;
  };

  Widget.prototype.renderTextFields = function () {
    var i18n = this.cfg.i18n;
    var vc = this.variantCfg();
    var self = this;
    this.textWrap.innerHTML = '';
    if (!vc.textCount) return;

    var textSlots = vc.textCount;

    if (vc.spotifyEnabled) {
      var toggleId = 'gs-spotify-' + this.cfg.productId;
      var toggle = el('label', { class: 'gs-spotify-toggle', for: toggleId });
      this.spotifyCheckbox = el('input', { type: 'checkbox', id: toggleId });
      this.spotifyCheckbox.checked = this.spotifyChecked;
      toggle.appendChild(this.spotifyCheckbox);
      toggle.appendChild(el('span', { text: i18n.spotifyToggle }));
      this.textWrap.appendChild(toggle);
      this.spotifyCheckbox.addEventListener('change', function () {
        self.spotifyChecked = self.spotifyCheckbox.checked;
        self.renderTextFields();
        self.syncFormProperties();
      });
      if (this.spotifyChecked) textSlots = vc.textCount - 1;
    }

    for (var n = 1; n <= textSlots; n++) {
      (function (idx) {
        var isMorse = !!self.cfg.morseEnabled;
        var label = isMorse
          ? i18n.morseFieldLabel.replace('{max}', self.cfg.textMaxLength)
          : i18n.textFieldLabel.replace('{n}', idx);
        var placeholder = isMorse ? i18n.morsePlaceholder : i18n.textPlaceholder.replace('{max}', self.cfg.textMaxLength);
        var field = el('div', { class: 'gs-field' });
        field.appendChild(el('label', { class: 'gs-field__label', for: 'gs-text-' + idx, text: label }));
        var input = el('input', {
          type: 'text', id: 'gs-text-' + idx,
          class: 'gs-field__input', autocomplete: 'off', placeholder: placeholder,
        });
        // no morse o corte e feito no JS (o maxlength contaria os espacos)
        if (!isMorse) input.setAttribute('maxlength', String(self.cfg.textMaxLength));
        input.value = self.textValues[idx] || '';
        field.appendChild(input);

        var morseOut = null, morseHint = null;
        if (isMorse && i18n.morseLatinOnly && !/translation missing/i.test(i18n.morseLatinOnly)) {
          // sem isso, quem digita em grego/japones nao entende por que nada aparece
          morseHint = el('p', { class: 'gs-error', role: 'alert', hidden: '', text: i18n.morseLatinOnly });
          field.appendChild(morseHint);
        }
        if (isMorse) {
          field.appendChild(el('label', { class: 'gs-field__label gs-field__label--morse', text: i18n.morseOutputLabel }));
          morseOut = el('div', { class: 'gs-morse-output', 'aria-live': 'polite' });
          updateMorseOutput(morseOut, input.value, i18n.morseOutputEmpty);
          field.appendChild(morseOut);
        }

        input.addEventListener('input', function () {
          // emoji vira coracao na hora (digitado ou colado), sem perder o cursor
          var raw = input.value;
          var semInvalidos = self.cleanText(raw);
          if (morseHint) {
            // compara so o que nao e espaco: o corte por limite nao dispara o aviso
            morseHint.hidden = semInvalidos.replace(/\s+/g, '') === raw.replace(/\s+/g, '');
          }
          var clean = self.limitText(semInvalidos);
          if (clean !== raw) {
            var pos = input.selectionStart;
            input.value = clean;
            if (typeof pos === 'number') {
              var p = Math.max(0, Math.min(clean.length, pos + (clean.length - raw.length)));
              try { input.setSelectionRange(p, p); } catch (e) {}
            }
          }
          self.textValues[idx] = input.value;
          self.emptyTextOk = false;
          if (morseOut) updateMorseOutput(morseOut, input.value, i18n.morseOutputEmpty);
          self.syncFormProperties();
        });
        self.textWrap.appendChild(field);
      })(n);
    }

    if (vc.spotifyEnabled && this.spotifyChecked) {
      var sf = el('div', { class: 'gs-field' });
      sf.appendChild(el('label', { class: 'gs-field__label', for: 'gs-spotify-url', text: i18n.spotifyLabel }));
      this.spotifyInput = el('input', {
        type: 'url', id: 'gs-spotify-url', class: 'gs-field__input',
        placeholder: i18n.spotifyPlaceholder, autocomplete: 'off', inputmode: 'url',
      });
      this.spotifyInput.value = this.spotifyValue;
      this.spotifyError = el('p', { class: 'gs-error', role: 'alert', hidden: '' });
      var self2 = this;
      this.spotifyInput.addEventListener('input', function () {
        self2.spotifyValue = self2.spotifyInput.value;
        self2.spotifyError.hidden = true;
        self2.syncFormProperties();
      });
      sf.appendChild(this.spotifyInput);
      sf.appendChild(this.spotifyError);
      this.textWrap.appendChild(sf);
    }
  };

  /**
   * Reescreve os hidden inputs de properties dentro do formulario real.
   * Campos que deixaram de existir (variante com menos lados, spotify
   * desmarcado) sao REMOVIDOS do form — nada de dados ocultos antigos.
   */
  /** limpa emoji do texto conforme o tipo de produto (coracao vs remover). */
  /**
   * Corta o texto no limite do produto. No morse o ESPACO NAO CONTA (a peca
   * ignora espacos na hora de montar as contas), entao o limite vale so para
   * os caracteres uteis; nos demais acessorios o espaco e gravado e conta.
   */
  Widget.prototype.limitText = function (s) {
    var max = parseInt(this.cfg.textMaxLength, 10) || 0;
    if (!max) return s;
    if (!this.cfg.morseEnabled) return s.slice(0, max);
    var out = '', usados = 0;
    for (var i = 0; i < s.length; i++) {
      var ch = s.charAt(i);
      if (!/\s/.test(ch)) {
        if (usados >= max) break;
        usados++;
      }
      out += ch;
    }
    return out.slice(0, max * 3); // guarda contra colagem absurda de espacos
  };

  Widget.prototype.cleanText = function (s) {
    if (!this.cfg.morseEnabled) return replaceEmoji(s, HEART);
    // morse: so alfabeto ocidental, espaco e "&"; fecha o espaco duplo que
    // sobra no lugar do que foi descartado
    var out = onlyMorseLetters(String(s || ''));
    return out === s ? out : out.replace(/  +/g, ' ');
  };

  Widget.prototype.syncFormProperties = function () {
    if (!this.form) return;
    var forms = this.allForms();
    forms.forEach(function (f) {
      qsa(f, 'input[data-gs-prop]').forEach(function (node) { node.remove(); });
    });

    function add(name, value) {
      if (!value) return;
      forms.forEach(function (f) {
        var input = el('input', { type: 'hidden', 'data-gs-prop': '', name: 'properties[' + name + ']' });
        input.value = value;
        f.appendChild(input);
      });
    }

    var self0 = this;
    var multiPhoto = this.photoCount() > 1;
    var anyPhotoUploaded = false;
    (this.photoSlots || []).forEach(function (slot) {
      if (!slot.upload) return;
      anyPhotoUploaded = true;
      if (multiPhoto) {
        add(self0.propName(self0.prop('photoSlot', slot.index)), slot.upload.publicUrl);
        add(self0.prop('uploadIdSlot', slot.index), slot.upload.uploadId);
      } else {
        add(self0.propName(self0.prop('photo')), slot.upload.publicUrl);
        add(self0.prop('uploadId'), slot.upload.uploadId);
      }
    });
    if (!anyPhotoUploaded && this.noPhotoSkip) {
      // cliente pulou a foto: marca visivel p/ fornecedor + gatilho da tag 📷SEM-FOTO
      add(this.propName(this.prop('noPhoto')), this.prop('noPhotoValue'));
    }

    var vc = this.variantCfg();
    var textSlots = vc.textCount - (vc.spotifyEnabled && this.spotifyChecked ? 1 : 0);
    for (var n = 1; n <= textSlots; n++) {
      var value = this.limitText(this.cleanText((this.textValues[n] || '').trim())).trim();
      add(this.propName(this.prop('textSide', n)), value);
      if (this.cfg.morseEnabled && n === 1) {
        // produto morse tem 1 campo so; a traducao acompanha o texto desse campo
        add(this.propName(this.prop('morseCode')), textToMorse(value));
      }
    }
    if (vc.spotifyEnabled && this.spotifyChecked && isSpotifyUrl(this.spotifyValue)) {
      add(this.propName(this.prop('spotify')), this.spotifyValue.trim());
    }
  };

  // ----------------------------------------------------- variante atual ---

  Widget.prototype.watchVariant = function () {
    var self = this;
    function check() {
      var id = self.readVariantId();
      if (id && id !== self.currentVariantId) {
        self.currentVariantId = id;
        self.renderTextFields();
        self.syncFormProperties();
      }
    }
    if (this.form) {
      this.form.addEventListener('change', function () { setTimeout(check, 0); });
    }
    document.addEventListener('change', function () { setTimeout(check, 0); });
    // fallback robusto: temas que trocam o input[name=id] sem disparar change
    setInterval(check, 750);
  };

  // ------------------------------------------------------ gate do submit --

  Widget.prototype.hookSubmit = function () {
    if (!this.form) return;
    var self = this;
    this.allForms().forEach(function (form) {
      form.addEventListener('submit', function (e) {
        if (!self.validateBeforeSubmit()) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, true); // capture: roda antes dos handlers AJAX do tema

      // botoes submit: bloqueia clique duplo durante upload
      qsa(form, 'button[type="submit"], [name="add"]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          if (self.uploading) { e.preventDefault(); e.stopImmediatePropagation(); }
          else if (!self.validateBeforeSubmit()) { e.preventDefault(); e.stopImmediatePropagation(); }
        }, true);
      });
    });
  };

  Widget.prototype.validateBeforeSubmit = function () {
    // testMode marca as properties com "_Teste" mas NAO desativa o gate:
    // o pop-up/exigencia de foto funcionam igual em homologacao.
    var vc = this.variantCfg();
    var i18n = this.cfg.i18n;
    var multiPhoto = this.photoCount() > 1;

    if (this.cfg.photoEnabled && multiPhoto) {
      // produtos multi-foto (ex.: Pulseira Duo) nao usam o modal pre-carrinho
      // (ele assume 1 foto so): validacao direta, todos os slots obrigatorios
      if (vc.photoRequired && !this.skipOnce) {
        var missing = this.photoSlots.filter(function (s) { return !s.upload; });
        if (missing.length) {
          if (this.photoGroupError) {
            this.photoGroupError.textContent = i18n.photoRequiredErrorMulti || i18n.photoRequiredError;
            this.photoGroupError.hidden = false;
          }
          this.container.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }
      }
    } else if (this.cfg.photoEnabled && !(this.photoSlots[0] && this.photoSlots[0].upload) && !this.skipOnce && !this.noPhotoSkip) {
      if (vc.photoRequired) {
        if (this.cfg.precartModal && i18n.precartTitle) this.openPrecart(false);
        else {
          this.showError(i18n.photoRequiredError);
          this.container.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }
      if (this.cfg.precartModal && i18n.precartTitle) {
        this.openPrecart(true);
        return false;
      }
    }
    if (vc.spotifyEnabled && this.spotifyChecked) {
      if (!isSpotifyUrl(this.spotifyValue)) {
        if (this.spotifyError) {
          this.spotifyError.textContent = i18n.spotifyInvalid;
          this.spotifyError.hidden = false;
        }
        this.container.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
    }
    // acessorio com gravacao e algum lado em branco: nao bloqueia, so confirma
    if (!this.cfg.morseEnabled && !this.emptyTextOk && this.hasEmptyTextCopy()) {
      var vazios = this.emptyTextSides();
      if (vazios.length) {
        this.openEmptyTextNotice(vazios[0]);
        return false;
      }
    }
    this.syncFormProperties();
    return true;
  };

  // -------------------------------------------- modal pre-carrinho (v2) ---

  /** Clica no botao real de adicionar ao carrinho (mantem o fluxo AJAX do tema). */
  Widget.prototype.triggerAddToCart = function () {
    if (!this.form) return;
    var btn = this.form.querySelector('button[type="submit"], [name="add"]');
    if (btn) btn.click();
    else if (this.form.requestSubmit) this.form.requestSubmit();
    else this.form.submit();
  };

  /**
   * "BEFORE YOU ADD TO CART..." — aparece quando o cliente tenta adicionar
   * sem foto. allowSkip=true mostra tambem "SKIP & ADD TO CART" (so quando a
   * foto NAO e obrigatoria).
   */
  Widget.prototype.openPrecart = function (allowSkip) {
    if (this.precartOverlay) return;
    var i18n = this.cfg.i18n;
    var self = this;

    var overlay = el('div', { class: 'gs-modal-overlay', role: 'dialog', 'aria-modal': 'true', 'aria-label': i18n.precartTitle });
    var modal = el('div', { class: 'gs-modal gs-precart' });

    var close = el('button', { type: 'button', class: 'gs-modal__close', 'aria-label': i18n.cancel, html: svgX() });
    modal.appendChild(close);
    modal.appendChild(el('h3', { class: 'gs-modal__title', text: i18n.precartTitle }));
    var body = allowSkip ? i18n.precartBody : i18n.precartBodyRequired;
    if (body) modal.appendChild(el('p', { class: 'gs-precart__body', text: body }));

    var actions = el('div', { class: 'gs-precart__actions' });
    if (allowSkip) {
      var skip = el('button', { type: 'button', class: 'gs-btn gs-btn--ghost', text: i18n.precartSkip });
      skip.addEventListener('click', function () {
        self.closePrecart();
        self.skipOnce = true;
        self.noPhotoSkip = true; // gera a property "Foto pendente" neste add
        self.syncFormProperties();
        self.triggerAddToCart();
        setTimeout(function () { self.skipOnce = false; }, 2000);
      });
      actions.appendChild(skip);
    }
    var up = el('button', { type: 'button', class: 'gs-btn gs-btn--primary', text: i18n.precartUpload });
    up.addEventListener('click', function () {
      self.closePrecart();
      self.pendingAddToCart = true; // apos confirmar a foto, adiciona sozinho
      self.photoSlots[0].fileInput.click(); // precart so roda com 1 foto (ver validateBeforeSubmit)
    });
    actions.appendChild(up);
    modal.appendChild(actions);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    document.body.classList.add('gs-no-scroll');

    close.addEventListener('click', function () { self.closePrecart(); });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) self.closePrecart(); });
    this.precartKeyHandler = function (e) { if (e.key === 'Escape') self.closePrecart(); };
    document.addEventListener('keydown', this.precartKeyHandler);
    this.precartOverlay = overlay;
    up.focus();
  };

  /** lados de texto que a variante oferece e o cliente deixou em branco. */
  Widget.prototype.emptyTextSides = function () {
    var vc = this.variantCfg();
    var slots = vc.textCount - (vc.spotifyEnabled && this.spotifyChecked ? 1 : 0);
    var vazios = [];
    for (var n = 1; n <= slots; n++) {
      if (!this.cleanText((this.textValues[n] || '').trim())) vazios.push(n);
    }
    return vazios;
  };

  /** so mostra o aviso se o idioma tiver os textos (senao segue como antes). */
  Widget.prototype.hasEmptyTextCopy = function () {
    var i18n = this.cfg.i18n;
    return ['emptyTextTitle', 'emptyTextBody', 'emptyTextAdd', 'emptyTextContinue'].every(function (k) {
      var v = i18n[k];
      return typeof v === 'string' && v && !/translation missing/i.test(v);
    });
  };

  /**
   * Aviso (nao bloqueio) para acessorio com gravacao em que algum lado ficou
   * em branco: a peca vai sem gravacao nesse lado, no estilo original. O
   * cliente escolhe escrever o texto ou seguir assim. Mesmo visual e mesmo
   * fechamento do pre-carrinho (closePrecart).
   */
  Widget.prototype.openEmptyTextNotice = function (firstEmpty) {
    if (this.precartOverlay) return;
    var i18n = this.cfg.i18n;
    var self = this;

    var overlay = el('div', { class: 'gs-modal-overlay', role: 'dialog', 'aria-modal': 'true', 'aria-label': i18n.emptyTextTitle });
    var modal = el('div', { class: 'gs-modal gs-precart' });
    var close = el('button', { type: 'button', class: 'gs-modal__close', 'aria-label': i18n.cancel, html: svgX() });
    modal.appendChild(close);
    modal.appendChild(el('h3', { class: 'gs-modal__title', text: i18n.emptyTextTitle }));
    modal.appendChild(el('p', { class: 'gs-precart__body', text: i18n.emptyTextBody }));

    var actions = el('div', { class: 'gs-precart__actions' });
    var seguir = el('button', { type: 'button', class: 'gs-btn gs-btn--ghost', text: i18n.emptyTextContinue });
    seguir.addEventListener('click', function () {
      self.closePrecart();
      self.emptyTextOk = true;
      self.triggerAddToCart();
    });
    var escrever = el('button', { type: 'button', class: 'gs-btn gs-btn--primary', text: i18n.emptyTextAdd });
    escrever.addEventListener('click', function () {
      self.closePrecart();
      var campo = qs(self.root, '#gs-text-' + firstEmpty);
      if (campo) {
        campo.scrollIntoView({ behavior: 'smooth', block: 'center' });
        campo.focus();
      }
    });
    actions.appendChild(seguir);
    actions.appendChild(escrever);
    modal.appendChild(actions);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    document.body.classList.add('gs-no-scroll');

    close.addEventListener('click', function () { self.closePrecart(); });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) self.closePrecart(); });
    this.precartKeyHandler = function (e) { if (e.key === 'Escape') self.closePrecart(); };
    document.addEventListener('keydown', this.precartKeyHandler);
    this.precartOverlay = overlay;
    escrever.focus();
  };

  Widget.prototype.closePrecart = function () {
    if (!this.precartOverlay) return;
    document.removeEventListener('keydown', this.precartKeyHandler);
    document.body.classList.remove('gs-no-scroll');
    this.precartOverlay.remove();
    this.precartOverlay = null;
  };

  /** slot opcional; sem ele usa o 1o slot (compat com produtos de 1 foto). */
  Widget.prototype.showError = function (msg, slot) {
    var box = (slot || this.photoSlots[0] || {}).errorBox;
    if (!box) return;
    box.textContent = msg;
    box.hidden = false;
  };
  Widget.prototype.clearError = function (slot) {
    var box = (slot || this.photoSlots[0] || {}).errorBox;
    if (box) box.hidden = true;
  };

  // ------------------------------------------------------- fluxo da foto --

  Widget.prototype.onFileChosen = function (file, slot) {
    var i18n = this.cfg.i18n;
    this.clearError(slot);

    if (file.size > MAX_BYTES) { this.showError(i18n.tooLarge, slot); return; }
    var typeOk = /^image\/(jpeg|png|webp|heic|heif)$/.test(file.type) ||
      /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || '');
    if (!typeOk) { this.showError(i18n.invalidType, slot); return; }

    var self = this;
    decodeImage(file).then(function (img) {
      if (self.cfg.editorEnabled === false) self.directUpload(file, img, slot);
      else new Editor(self, file, img, slot);
    }).catch(function () {
      self.showError(i18n.decodeError, slot);
    });
  };

  /**
   * Envia original+processed ao Worker (compartilhado pelo editor e pelo
   * envio direto sem editor). Retorna Promise com a resposta da API.
   */
  Widget.prototype.performUpload = function (file, processed, onProgress) {
    var self = this;
    var form = new FormData();
    form.append('original', file, file.name || 'foto');
    form.append('processed', processed.blob, 'processed.jpg');
    form.append('crop_data', JSON.stringify(processed.cropData));
    form.append('product_id', String(self.cfg.productId));
    form.append('variant_id', self.currentVariantId || '');
    form.append('session_id', sessionId());
    form.append('width', String(processed.cropData.sourceWidth));
    form.append('height', String(processed.cropData.sourceHeight));
    // textos atuais (copia de suporte no D1; o pedido leva as properties)
    var vc = self.variantCfg();
    var textData = {};
    var slots = vc.textCount - (vc.spotifyEnabled && self.spotifyChecked ? 1 : 0);
    for (var n = 1; n <= slots; n++) {
      if (self.textValues[n]) textData[self.prop('textSide', n)] = self.textValues[n];
    }
    if (self.spotifyChecked && isSpotifyUrl(self.spotifyValue)) {
      textData[self.prop('spotify')] = self.spotifyValue.trim();
    }
    if (Object.keys(textData).length) form.append('text_data', JSON.stringify(textData));

    var withToken = self.cfg.turnstileSiteKey
      ? getTurnstileToken(self.cfg.turnstileSiteKey).then(function (t) { if (t) form.append('turnstile_token', t); })
      : Promise.resolve();
    return withToken.then(function () {
      return uploadWithProgress(self.cfg.workerBaseUrl + '/api/uploads', form, onProgress);
    });
  };

  /** editorEnabled=false: recorte central automatico e envio imediato. */
  Widget.prototype.directUpload = function (file, image, slot) {
    var self = this;
    var i18n = this.cfg.i18n;
    if (this.uploading) return;
    this.uploading = true;
    slot.uploadBtn.disabled = true;
    var oldText = slot.uploadBtnText.textContent;
    slot.uploadBtnText.textContent = i18n.sending;

    Promise.all([exportCentered(image, this.cfg), shrinkOriginal(image, file)]).then(function (res) {
      var processed = res[0];
      return self.performUpload(res[1], processed, function (pct) {
        slot.uploadBtnText.textContent = i18n.sending + ' ' + Math.round(pct * 100) + '%';
      }).then(function (data) {
        self.uploading = false;
        slot.uploadBtn.disabled = false;
        self.setUploaded(data, processed.thumb, slot);
        if (image && image.close) try { image.close(); } catch (e) {}
      });
    }).catch(function (err) {
      self.uploading = false;
      slot.uploadBtn.disabled = false;
      slot.uploadBtnText.textContent = oldText;
      self.showError(i18n.uploadError + (err && err.gsCode ? ' (' + err.gsCode + ')' : ''), slot);
      if (image && image.close) try { image.close(); } catch (e) {}
    });
  };

  /**
   * Depois do upload, so adiciona ao carrinho sozinho se nao faltar mais nada
   * para o cliente preencher:
   *  - produto multi-foto: todas as fotos ja enviadas (senao adicionaria com
   *    a Foto 2 faltando);
   *  - produto com gravacao de texto ou Spotify: NAO adiciona sozinho, senao
   *    o pedido iria sem o texto que o cliente ainda vai digitar;
   *  - so uma vez por visita, para trocar a foto depois nao adicionar de novo.
   */
  Widget.prototype.canAutoAddAfterUpload = function () {
    if (this.autoAddDone) return false;
    var vc = this.variantCfg();
    if (vc.textCount > 0 || vc.spotifyEnabled) return false;
    for (var i = 0; i < this.photoSlots.length; i++) {
      if (!this.photoSlots[i].upload) return false;
    }
    return true;
  };

  Widget.prototype.setUploaded = function (data, thumbDataUrl, slot) {
    slot.upload = { uploadId: data.upload_id, publicUrl: data.public_url };
    this.noPhotoSkip = false; // com foto, a marca "Foto pendente" sai
    if (this.photoGroupError) this.photoGroupError.hidden = true;
    var i18n = this.cfg.i18n;
    slot.preview.innerHTML = '';
    var shapeClass = this.cfg.cropShape === 'circle' ? ' gs-preview__img--circle' : '';
    var img = el('img', { class: 'gs-preview__img' + shapeClass, alt: i18n.photoConfirmed });
    img.src = thumbDataUrl;
    slot.preview.appendChild(img);
    var meta = el('div', { class: 'gs-preview__meta' });
    meta.appendChild(el('p', { class: 'gs-preview__ok', html: svgCheck() + '<span>' + i18n.photoConfirmed + '</span>' }));
    var change = el('button', { type: 'button', class: 'gs-link-btn', text: i18n.changePhoto });
    change.addEventListener('click', function () { slot.fileInput.value = ''; slot.fileInput.click(); });
    meta.appendChild(change);
    slot.preview.appendChild(meta);
    slot.preview.hidden = false;
    slot.uploadBtn.classList.add('gs-upload-btn--done');
    slot.uploadBtnText.textContent = i18n.changePhoto;
    this.clearError(slot);
    this.syncFormProperties();

    // foto confirmada -> vai direto para o carrinho, poupando um clique
    if (this.pendingAddToCart || this.canAutoAddAfterUpload()) {
      this.pendingAddToCart = false;
      this.autoAddDone = true;
      this.triggerAddToCart();
    }
  };

  // ------------------------------------------------------------ decode ----

  // A ORIGINAL vai ao R2 so como copia de seguranca privada: o pedido usa a
  // processada. Mandar o arquivo cru do celular (3-12 MB) era o que deixava o
  // upload lento no 4G. Aqui ela sobe reduzida a ORIGINAL_MAX_PX no lado maior,
  // o que ainda sobra para qualquer re-recorte da peca (saida de 1200 px).
  // Sai da imagem ja decodificada, entao a rotacao EXIF do celular vem certa.
  var ORIGINAL_MAX_PX = 2048;
  var ORIGINAL_KEEP_BYTES = 1536 * 1024; // abaixo disso ja e leve: sobe como veio

  function shrinkOriginal(image, file) {
    var w = image.width || image.naturalWidth, h = image.height || image.naturalHeight;
    var longSide = Math.max(w, h);
    if (!w || !h || (longSide <= ORIGINAL_MAX_PX && file.size <= ORIGINAL_KEEP_BYTES)) {
      return Promise.resolve(file);
    }
    var k = Math.min(1, ORIGINAL_MAX_PX / longSide);
    var canvas = document.createElement('canvas');
    canvas.width = Math.round(w * k);
    canvas.height = Math.round(h * k);
    var ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    return new Promise(function (resolve) {
      canvas.toBlob(function (blob) {
        // qualquer falha: sobe a original como antes (nunca trava o pedido)
        if (!blob || blob.size >= file.size) { resolve(file); return; }
        var base = file.name || 'foto';
        var dot = base.lastIndexOf('.');
        if (dot > 0) base = base.slice(0, dot);
        try { resolve(new File([blob], base + '.jpg', { type: 'image/jpeg' })); }
        catch (e) { resolve(blob); }
      }, 'image/jpeg', 0.88);
    });
  }

  // a miniatura aparece com 72 px na pagina: gera-la com 240 px (e nao com os
  // 1200 px da processada) poupa um segundo encode grande antes do envio
  function smallThumb(canvas) {
    var t = document.createElement('canvas');
    t.width = 240; t.height = 240;
    var c = t.getContext('2d');
    c.imageSmoothingQuality = 'high';
    c.drawImage(canvas, 0, 0, 240, 240);
    return t.toDataURL('image/jpeg', 0.8);
  }

  function decodeImage(file) {
    // createImageBitmap ja normaliza a orientacao EXIF nos navegadores atuais
    if (window.createImageBitmap) {
      return createImageBitmap(file, { imageOrientation: 'from-image' }).catch(function () {
        return decodeViaImg(file);
      });
    }
    return decodeViaImg(file);
  }

  function decodeViaImg(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('decode')); };
      img.src = url;
    });
  }

  // ------------------------------------------------------------- Editor ---

  function Editor(widget, file, image, slot) {
    this.widget = widget;
    this.cfg = widget.cfg;
    this.file = file;
    this.image = image;
    this.slot = slot;
    this.imgW = image.width || image.naturalWidth;
    this.imgH = image.height || image.naturalHeight;
    this.offsetX = 0;   // em px, no referencial JA rotacionado (alinhado a imagem)
    this.offsetY = 0;
    this.quarter = 0;   // giros de 90 graus (0..3)
    this.fine = 0;      // ajuste fino -45..45 graus
    this.tools = Array.isArray(this.cfg.editorTools) && this.cfg.editorTools.length
      ? this.cfg.editorTools : ['zoom']; // rotacao so quando pedida via editor_tools
    this.pointers = {};
    this.lastFocus = document.activeElement;
    this.buildModal();
    this.layout();
    this.bind();
    this.render();
  }

  Editor.prototype.theta = function () {
    return ((this.quarter * 90 + this.fine) * Math.PI) / 180;
  };

  /** Fator de cobertura do recorte quadrado quando a imagem esta girada. */
  Editor.prototype.cov = function () {
    var t = this.theta();
    return Math.abs(Math.cos(t)) + Math.abs(Math.sin(t));
  };

  Editor.prototype.buildModal = function () {
    var i18n = this.cfg.i18n;
    var self = this;
    var shape = this.cfg.cropShape || 'circle';

    this.overlay = el('div', { class: 'gs-modal-overlay', role: 'dialog', 'aria-modal': 'true', 'aria-label': i18n.editorTitle });
    var modal = el('div', { class: 'gs-modal' });

    modal.appendChild(el('h3', { class: 'gs-modal__title', text: i18n.editorTitle }));
    modal.appendChild(el('p', { class: 'gs-modal__hint', text: i18n.editorHint }));

    this.stage = el('div', { class: 'gs-stage', tabindex: '0', 'aria-label': i18n.editorTitle });
    this.canvas = el('canvas', { class: 'gs-stage__canvas' });
    this.stage.appendChild(this.canvas);
    this.mask = el('div', { class: 'gs-stage__mask' + (shape === 'circle' ? ' gs-stage__mask--circle' : '') });
    this.stage.appendChild(this.mask);
    modal.appendChild(this.stage);

    if (this.tools.indexOf('zoom') !== -1) {
      var zoomWrap = el('div', { class: 'gs-zoom' });
      zoomWrap.appendChild(el('span', { class: 'gs-zoom__icon', html: svgMinus(), 'aria-hidden': 'true' }));
      this.slider = el('input', {
        type: 'range', min: '0', max: '100', value: '0', step: '1',
        class: 'gs-zoom__slider', 'aria-label': i18n.zoomLabel,
      });
      zoomWrap.appendChild(this.slider);
      zoomWrap.appendChild(el('span', { class: 'gs-zoom__icon', html: svgPlus(), 'aria-hidden': 'true' }));
      modal.appendChild(zoomWrap);
    }

    if (this.tools.indexOf('rotate') !== -1) {
      var rotWrap = el('div', { class: 'gs-rotate' });
      this.rotateBtn = el('button', {
        type: 'button', class: 'gs-rotate__btn', 'aria-label': i18n.rotateLabel || 'Rotate 90',
        title: i18n.rotateLabel || 'Rotate 90', html: svgRotate(),
      });
      rotWrap.appendChild(this.rotateBtn);
      this.fineSlider = el('input', {
        type: 'range', min: '-45', max: '45', value: '0', step: '1',
        class: 'gs-rotate__slider', 'aria-label': i18n.straightenLabel || 'Straighten',
      });
      rotWrap.appendChild(this.fineSlider);
      this.degreeOut = el('span', { class: 'gs-rotate__deg', text: '0°' });
      rotWrap.appendChild(this.degreeOut);
      modal.appendChild(rotWrap);
    }

    this.progress = el('div', { class: 'gs-progress', hidden: '' });
    this.progressBar = el('div', { class: 'gs-progress__bar' });
    this.progress.appendChild(this.progressBar);
    this.progressText = el('p', { class: 'gs-progress__text', text: i18n.sending });
    modal.appendChild(this.progress);
    modal.appendChild(this.progressText);
    this.progressText.hidden = true;

    this.modalError = el('p', { class: 'gs-error', role: 'alert', hidden: '' });
    modal.appendChild(this.modalError);

    var actions = el('div', { class: 'gs-modal__actions' });
    this.cancelBtn = el('button', { type: 'button', class: 'gs-btn gs-btn--ghost', text: i18n.cancel });
    // vindo do modal pre-carrinho, confirmar tambem adiciona ao carrinho
    var confirmLabel = (this.widget.pendingAddToCart && i18n.confirmAdd) ? i18n.confirmAdd : i18n.confirm;
    this.confirmBtn = el('button', { type: 'button', class: 'gs-btn gs-btn--primary', text: confirmLabel });
    actions.appendChild(this.cancelBtn);
    actions.appendChild(this.confirmBtn);
    modal.appendChild(actions);

    this.overlay.appendChild(modal);
    document.body.appendChild(this.overlay);
    document.body.classList.add('gs-no-scroll');

    this.cancelBtn.addEventListener('click', function () { self.close(); });
    this.overlay.addEventListener('click', function (e) { if (e.target === self.overlay) self.close(); });
    this.confirmBtn.addEventListener('click', function () { self.confirm(); });

    // focus trap simples
    this.keyHandler = function (e) {
      if (e.key === 'Escape') { self.close(); return; }
      if (e.key === 'Tab') {
        var focusables = qsa(self.overlay, 'button, input, [tabindex="0"]');
        var first = focusables[0], last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', this.keyHandler);
    this.stage.focus();
  };

  Editor.prototype.layout = function () {
    var size = Math.min(this.stage.clientWidth, 420);
    this.viewSize = size; // area de recorte quadrada SxS em px CSS
    var dpr = Math.min(window.devicePixelRatio || 1, 3);
    this.dpr = dpr;
    this.canvas.width = size * dpr;
    this.canvas.height = size * dpr;
    this.canvas.style.width = size + 'px';
    this.canvas.style.height = size + 'px';
    var cov = this.cov();
    this.coverScale = Math.max((size * cov) / this.imgW, (size * cov) / this.imgH); // cobre o recorte
    // permite REDUZIR alem da cobertura: piso = foto inteira dentro do
    // recorte (diagonal), sobrando espaco vazado em branco
    this.minScale = Math.min(this.coverScale, size / Math.hypot(this.imgW, this.imgH));
    this.maxScale = this.coverScale * 5;
    this.scale = this.coverScale; // comeca cobrindo; o cliente decide reduzir
    this.ctx = this.canvas.getContext('2d');
    if (this.slider) {
      this.slider.value = String(Math.round(((this.scale - this.minScale) / (this.maxScale - this.minScale || 1)) * 100));
    }
  };

  Editor.prototype.clamp = function () {
    // os cantos do recorte, girados para o referencial da imagem, ocupam S*cov
    var half = (this.viewSize * this.cov()) / 2;
    var maxX = Math.max(0, (this.imgW * this.scale) / 2 - half);
    var maxY = Math.max(0, (this.imgH * this.scale) / 2 - half);
    this.offsetX = Math.min(maxX, Math.max(-maxX, this.offsetX));
    this.offsetY = Math.min(maxY, Math.max(-maxY, this.offsetY));
  };

  /** Recalcula limites apos mudar a rotacao, preservando o zoom atual. */
  Editor.prototype.applyRotation = function () {
    var S = this.viewSize, cov = this.cov();
    this.coverScale = Math.max((S * cov) / this.imgW, (S * cov) / this.imgH);
    this.minScale = Math.min(this.coverScale, S / Math.hypot(this.imgW, this.imgH));
    this.maxScale = this.coverScale * 5;
    this.scale = Math.min(this.maxScale, Math.max(this.minScale, this.scale));
    this.clamp();
    if (this.slider) {
      this.slider.value = String(Math.round(((this.scale - this.minScale) / (this.maxScale - this.minScale || 1)) * 100));
    }
    if (this.degreeOut) this.degreeOut.textContent = (this.quarter * 90 + this.fine) + '°';
    this.render();
  };

  Editor.prototype.render = function () {
    var ctx = this.ctx, dpr = this.dpr, S = this.viewSize;
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, S, S);
    ctx.fillStyle = '#ffffff'; // espaco vazado fica BRANCO (igual ao export)
    ctx.fillRect(0, 0, S, S);
    ctx.translate(S / 2, S / 2);
    ctx.rotate(this.theta());
    ctx.translate(this.offsetX, this.offsetY);
    var w = this.imgW * this.scale, h = this.imgH * this.scale;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(this.image, -w / 2, -h / 2, w, h);
    ctx.restore();
  };

  /** Converte um deslocamento em px de tela para o referencial da imagem. */
  Editor.prototype.pan = function (dx, dy) {
    var t = this.theta(), cos = Math.cos(t), sin = Math.sin(t);
    this.offsetX += dx * cos + dy * sin;
    this.offsetY += -dx * sin + dy * cos;
  };

  Editor.prototype.setScale = function (next, cx, cy) {
    next = Math.min(this.maxScale, Math.max(this.minScale, next));
    if (cx === undefined) { cx = this.viewSize / 2; cy = this.viewSize / 2; }
    // zoom ao redor do ponto (cx, cy) — ponto convertido p/ referencial da imagem
    var t = this.theta(), cos = Math.cos(t), sin = Math.sin(t);
    var relX = cx - this.viewSize / 2, relY = cy - this.viewSize / 2;
    var px = relX * cos + relY * sin, py = -relX * sin + relY * cos;
    var ratio = next / this.scale;
    this.offsetX = (this.offsetX - px) * ratio + px;
    this.offsetY = (this.offsetY - py) * ratio + py;
    this.scale = next;
    this.clamp();
    if (this.slider) {
      this.slider.value = String(Math.round(((this.scale - this.minScale) / (this.maxScale - this.minScale || 1)) * 100));
    }
    this.render();
  };

  Editor.prototype.bind = function () {
    var self = this;

    if (this.slider) {
      this.slider.addEventListener('input', function () {
        var t = Number(self.slider.value) / 100;
        self.setScale(self.minScale + t * (self.maxScale - self.minScale));
      });
    }

    if (this.rotateBtn) {
      this.rotateBtn.addEventListener('click', function () {
        self.quarter = (self.quarter + 1) % 4;
        self.applyRotation();
      });
    }
    if (this.fineSlider) {
      this.fineSlider.addEventListener('input', function () {
        self.fine = Number(self.fineSlider.value) || 0;
        self.applyRotation();
      });
    }

    this.stage.addEventListener('wheel', function (e) {
      e.preventDefault();
      var rect = self.canvas.getBoundingClientRect();
      var factor = e.deltaY < 0 ? 1.08 : 1 / 1.08;
      self.setScale(self.scale * factor, e.clientX - rect.left, e.clientY - rect.top);
    }, { passive: false });

    this.stage.addEventListener('pointerdown', function (e) {
      e.preventDefault();
      self.stage.setPointerCapture(e.pointerId);
      self.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
    });

    this.stage.addEventListener('pointermove', function (e) {
      var ids = Object.keys(self.pointers);
      if (!self.pointers[e.pointerId]) return;
      if (ids.length === 1) {
        var p = self.pointers[e.pointerId];
        self.pan(e.clientX - p.x, e.clientY - p.y);
        p.x = e.clientX; p.y = e.clientY;
        self.clamp();
        self.render();
      } else if (ids.length === 2) {
        // pinca: escala pela variacao da distancia entre os dois dedos
        var other = ids[0] === String(e.pointerId) ? self.pointers[ids[1]] : self.pointers[ids[0]];
        var cur = self.pointers[e.pointerId];
        var before = Math.hypot(cur.x - other.x, cur.y - other.y);
        cur.x = e.clientX; cur.y = e.clientY;
        var after = Math.hypot(cur.x - other.x, cur.y - other.y);
        if (before > 0) {
          var rect = self.canvas.getBoundingClientRect();
          var midX = (cur.x + other.x) / 2 - rect.left;
          var midY = (cur.y + other.y) / 2 - rect.top;
          self.setScale(self.scale * (after / before), midX, midY);
        }
      }
    });

    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
      self.stage.addEventListener(evt, function (e) { delete self.pointers[e.pointerId]; });
    });

    // teclado: setas movem, +/- ampliam
    this.stage.addEventListener('keydown', function (e) {
      var step = e.shiftKey ? 20 : 5;
      var handled = true;
      switch (e.key) {
        case 'ArrowLeft': self.pan(step, 0); break;
        case 'ArrowRight': self.pan(-step, 0); break;
        case 'ArrowUp': self.pan(0, step); break;
        case 'ArrowDown': self.pan(0, -step); break;
        case '+': case '=': self.setScale(self.scale * 1.1); return;
        case '-': case '_': self.setScale(self.scale / 1.1); return;
        default: handled = false;
      }
      if (handled) { e.preventDefault(); self.clamp(); self.render(); }
    });

    window.addEventListener('resize', this.resizeHandler = function () {
      var keep = { scaleT: (self.scale - self.minScale) / (self.maxScale - self.minScale || 1), x: self.offsetX, y: self.offsetY };
      self.layout();
      self.scale = self.minScale + keep.scaleT * (self.maxScale - self.minScale);
      self.offsetX = keep.x; self.offsetY = keep.y;
      self.clamp();
      self.render();
    });
  };

  Editor.prototype.close = function () {
    document.removeEventListener('keydown', this.keyHandler);
    window.removeEventListener('resize', this.resizeHandler);
    document.body.classList.remove('gs-no-scroll');
    this.overlay.remove();
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    if (this.image && this.image.close) try { this.image.close(); } catch (e) {}
  };

  /** Gera a imagem processada (quadrada; a mascara circular e dado de recorte). */
  Editor.prototype.exportProcessed = function () {
    var out = this.cfg.outputSize || 1200;
    var S = this.viewSize;
    var k = out / S; // reproduz o palco em alta resolucao
    var canvas = document.createElement('canvas');
    canvas.width = out; canvas.height = out;
    var ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out, out);
    ctx.imageSmoothingQuality = 'high';
    ctx.setTransform(k, 0, 0, k, 0, 0);
    ctx.translate(S / 2, S / 2);
    ctx.rotate(this.theta());
    ctx.translate(this.offsetX, this.offsetY);
    var w = this.imgW * this.scale, h = this.imgH * this.scale;
    ctx.drawImage(this.image, -w / 2, -h / 2, w, h);
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    var deg = this.quarter * 90 + this.fine;
    var cropData = {
      scale: this.scale / this.minScale,
      rotation: deg,
      shape: this.cfg.cropShape || 'circle', outputSize: out,
      sourceWidth: this.imgW, sourceHeight: this.imgH,
    };
    if (deg === 0) { // posicao exata so faz sentido sem rotacao
      var sw = S / this.scale;
      cropData.x = (this.imgW / 2 - this.offsetX / this.scale - sw / 2) / this.imgW;
      cropData.y = (this.imgH / 2 - this.offsetY / this.scale - sw / 2) / this.imgH;
    }
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (!blob) { reject(new Error('toBlob')); return; }
        resolve({ blob: blob, cropData: cropData, thumb: smallThumb(canvas) });
      }, 'image/jpeg', 0.92);
    });
  };

  /** Recorte central automatico (usado quando o editor esta desativado). */
  function exportCentered(image, cfg) {
    var out = cfg.outputSize || 1200;
    var w = image.width || image.naturalWidth, h = image.height || image.naturalHeight;
    var side = Math.min(w, h);
    var sx = (w - side) / 2, sy = (h - side) / 2;
    var canvas = document.createElement('canvas');
    canvas.width = out; canvas.height = out;
    var ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out, out);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, sx, sy, side, side, 0, 0, out, out);
    var cropData = {
      x: sx / w, y: sy / h, scale: 1, rotation: 0,
      shape: cfg.cropShape || 'circle', outputSize: out,
      sourceWidth: w, sourceHeight: h,
    };
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (!blob) { reject(new Error('toBlob')); return; }
        resolve({ blob: blob, cropData: cropData, thumb: smallThumb(canvas) });
      }, 'image/jpeg', 0.92);
    });
  }

  Editor.prototype.confirm = function () {
    var self = this;
    var i18n = this.cfg.i18n;
    if (this.widget.uploading) return;
    this.widget.uploading = true;
    this.confirmBtn.disabled = true;
    this.cancelBtn.disabled = true;
    this.modalError.hidden = true;
    this.progress.hidden = false;
    this.progressText.hidden = false;
    this.progressBar.style.width = '10%';

    Promise.all([this.exportProcessed(), shrinkOriginal(this.image, this.file)]).then(function (res) {
      var processed = res[0];
      return self.widget.performUpload(res[1], processed, function (pct) {
        self.progressBar.style.width = Math.max(10, Math.round(pct * 100)) + '%';
      }).then(function (data) {
        self.progressBar.style.width = '100%';
        self.widget.uploading = false;
        self.close();
        self.widget.setUploaded(data, processed.thumb, self.slot); // pode auto-adicionar ao carrinho
      });
    }).catch(function (err) {
      self.widget.uploading = false;
      self.confirmBtn.disabled = false;
      self.cancelBtn.disabled = false;
      self.progress.hidden = true;
      self.progressText.hidden = true;
      self.modalError.textContent = i18n.uploadError + (err && err.gsCode ? ' (' + err.gsCode + ')' : '');
      self.modalError.hidden = false;
      self.confirmBtn.textContent = i18n.retry;
    });
  };

  function uploadWithProgress(url, formData, onProgress) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.timeout = 120000;
      xhr.upload.onprogress = function (e) { if (e.lengthComputable) onProgress(e.loaded / e.total); };
      xhr.onload = function () {
        var data = null;
        try { data = JSON.parse(xhr.responseText); } catch (e) {}
        if (xhr.status >= 200 && xhr.status < 300 && data && data.ok) resolve(data);
        else {
          var err = new Error('upload_failed');
          err.gsCode = data && data.error ? data.error : 'http_' + xhr.status;
          reject(err);
        }
      };
      xhr.onerror = function () { reject(new Error('network')); };
      xhr.ontimeout = function () { reject(new Error('timeout')); };
      xhr.send(formData);
    });
  }

  // Turnstile invisivel (carrega sob demanda apenas se houver site key)
  var turnstilePromise = null;
  function getTurnstileToken(siteKey) {
    if (!turnstilePromise) {
      turnstilePromise = new Promise(function (resolve) {
        var s = document.createElement('script');
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        s.async = true;
        s.onload = function () { resolve(); };
        s.onerror = function () { resolve(); };
        document.head.appendChild(s);
      });
    }
    return turnstilePromise.then(function () {
      return new Promise(function (resolve) {
        if (!window.turnstile) { resolve(null); return; }
        var holder = document.createElement('div');
        holder.style.position = 'fixed';
        holder.style.bottom = '8px';
        holder.style.right = '8px';
        holder.style.zIndex = '2147483647';
        document.body.appendChild(holder);
        var done = false;
        var timer = setTimeout(function () { if (!done) { done = true; holder.remove(); resolve(null); } }, 20000);
        try {
          window.turnstile.render(holder, {
            sitekey: siteKey,
            callback: function (token) {
              if (done) return;
              done = true; clearTimeout(timer);
              setTimeout(function () { holder.remove(); }, 500);
              resolve(token);
            },
            'error-callback': function () {
              if (done) return;
              done = true; clearTimeout(timer); holder.remove(); resolve(null);
            },
          });
        } catch (e) { clearTimeout(timer); holder.remove(); resolve(null); }
      });
    });
  }

  // -------------------------------------------------------------- icons ---

  function svgCamera() {
    return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>';
  }
  function svgCheck() {
    return '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
  }
  function svgPlus() {
    return '<svg width="14" height="14" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
  }
  function svgMinus() {
    return '<svg width="14" height="14" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>';
  }
  function svgX() {
    return '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
  }
  function svgRotate() {
    return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>';
  }

  // --------------------------------------------------------------- init ---

  function init() {
    qsa(document, '[data-gs-widget]').forEach(function (node) {
      if (!node.__gsWidget) node.__gsWidget = new Widget(node);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
